import React, { useState, useEffect, useRef } from "react";
import {
  Loader2, CheckCircle2, Unlock, AlertCircle, X, FileText,
  ExternalLink, Upload, CheckCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const SKY    = "#38BDF8";

// ── Admin: Pending Payment Receipts ──────────────────────────────────────────
export function PendingReceiptsPanel({ token, onRefresh }) {
  const [receipts, setReceipts]         = useState([]);   // receipt_uploaded — needs final approve
  const [awaiting, setAwaiting]         = useState([]);   // payment_pending — admin can upload
  const [loading, setLoading]           = useState(true);
  const [approving, setApproving]       = useState(null);
  const [uploading, setUploading]       = useState(null); // bid_id being uploaded
  const [previewUrl, setPreviewUrl]     = useState(null);
  const fileRef                         = useRef(null);
  const [pendingBidId, setPendingBidId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.marketAdminPendingReceipts(token);
      setReceipts(r.pending_receipts   || []);
      setAwaiting(r.awaiting_receipts  || []);
    } catch { toast.error("Failed to load pending receipts"); }
    finally   { setLoading(false); }
  }
  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line

  async function finalApprove(bidId, carrierName, driverName) {
    if (!window.confirm(`Approve payment from "${carrierName}" for driver "${driverName}"? This will unlock the driver's full profile for the carrier.`)) return;
    setApproving(bidId);
    try {
      await api.marketAdminFinalApprove(token, bidId);
      toast.success(`Transfer approved — ${driverName} unlocked for ${carrierName}`);
      load();
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Approval failed"); }
    finally { setApproving(null); }
  }

  function triggerUpload(bidId) {
    setPendingBidId(bidId);
    fileRef.current?.click();
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !pendingBidId) return;
    setUploading(pendingBidId);
    try {
      await api.marketAdminUploadReceiptForCarrier(token, pendingBidId, file);
      toast.success("Receipt uploaded — transaction completed automatically");
      load();
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(null); setPendingBidId(null); }
  }

  const totalBadge = receipts.length + awaiting.length;

  return (
    <div className="space-y-4">
      {/* Hidden file input for admin receipt upload */}
      <input ref={fileRef} type="file" className="hidden"
        accept="image/*,.pdf,.heic"
        onChange={handleFileChange} />

      <div>
        <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-0.5">Admin</p>
        <h3 className="font-head text-xl font-black uppercase tracking-[0.04em] flex items-center gap-2" style={{ color: ACCENT }}>
          Pending Payment Receipts
          {totalBadge > 0 && (
            <span className="flex items-center justify-center w-5 h-5 font-head text-[9px] font-black text-white"
              style={{ background: ACCENT }}>{totalBadge}</span>
          )}
        </h3>
        <p className="text-xs text-white/30 mt-0.5">
          Review carrier payment receipts and approve final transfers to unlock driver profiles. You can also upload a receipt on behalf of a carrier — this auto-completes the transaction.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} /></div>
      ) : (
        <>
          {/* ── Section 1: Awaiting Receipt (payment_pending) — admin can upload ── */}
          {awaiting.length > 0 && (
            <div className="space-y-3">
              <p className="font-head text-[9px] uppercase tracking-[0.25em] pb-1"
                style={{ color: SKY, borderBottom: "1px solid rgba(56,189,248,0.15)" }}>
                Awaiting Receipt — Upload on Carrier's Behalf
              </p>
              {awaiting.map((r) => (
                <div key={r.bid_id} className="p-4"
                  style={{ background: "#161618", border: "1px solid rgba(56,189,248,0.15)" }}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-head text-sm font-black text-white">{r.driver_name}</p>
                        <span className="font-head text-[9px] uppercase tracking-[0.15em] px-2 py-0.5"
                          style={{ background: "rgba(245,200,66,0.1)", color: GOLD, border: "1px solid rgba(245,200,66,0.25)" }}>
                          ${(r.placement_fee || 0).toLocaleString()}
                        </span>
                        <span className="font-head text-[8px] uppercase tracking-[0.1em] px-1.5 py-0.5"
                          style={{ background: "rgba(56,189,248,0.08)", color: SKY, border: "1px solid rgba(56,189,248,0.2)" }}>
                          Awaiting Payment
                        </span>
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                        Carrier: <strong className="text-white">{r.carrier_name}</strong>
                        {r.carrier_email && <span className="ml-2 opacity-50">{r.carrier_email}</span>}
                      </p>
                      {r.payment_pending_at && (
                        <p className="text-[10px] mt-0.5" style={{ color: "rgba(255,255,255,0.25)" }}>
                          Pending since: {new Date(r.payment_pending_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <button
                      data-testid={`admin-upload-receipt-btn-${r.bid_id}`}
                      disabled={uploading === r.bid_id}
                      onClick={() => triggerUpload(r.bid_id)}
                      className="flex items-center gap-1.5 px-4 py-2 font-head text-[9px] uppercase tracking-[0.15em] font-black transition-all disabled:opacity-40 hover:brightness-110 shrink-0"
                      style={{ background: "rgba(56,189,248,0.1)", color: SKY, border: "1px solid rgba(56,189,248,0.35)" }}>
                      {uploading === r.bid_id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Upload className="w-3 h-3" />}
                      Upload Receipt
                    </button>
                  </div>
                  <p className="text-[9px] mt-2 pl-0.5" style={{ color: "rgba(255,255,255,0.2)" }}>
                    Carrier sent receipt via WhatsApp? Upload it here — transaction completes automatically.
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* ── Section 2: Receipt Uploaded — needs final approve ── */}
          {receipts.length > 0 && (
            <div className="space-y-3" data-testid="pending-receipts-list">
              {awaiting.length > 0 && (
                <p className="font-head text-[9px] uppercase tracking-[0.25em] pb-1"
                  style={{ color: GOLD, borderBottom: "1px solid rgba(245,200,66,0.15)" }}>
                  Receipt Uploaded — Needs Your Approval
                </p>
              )}
              {receipts.map((r) => (
                <div key={r.bid_id} className="p-4 space-y-3"
                  style={{ background: "#161618", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-head text-sm font-black text-white">{r.driver_name}</p>
                        <span className="font-head text-[9px] uppercase tracking-[0.15em] px-2 py-0.5"
                          style={{ background: "rgba(245,200,66,0.1)", color: GOLD, border: "1px solid rgba(245,200,66,0.25)" }}>
                          ${(r.placement_fee || 0).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                        Carrier: <strong className="text-white">{r.carrier_name}</strong>
                        {r.carrier_email && <span className="ml-2 opacity-50">{r.carrier_email}</span>}
                      </p>
                      <p className="text-[10px] mt-0.5" style={{ color: "rgba(255,255,255,0.25)" }}>
                        Receipt uploaded: {r.receipt_uploaded_at ? new Date(r.receipt_uploaded_at).toLocaleString() : "—"}
                      </p>
                    </div>
                    <button onClick={() => finalApprove(r.bid_id, r.carrier_name, r.driver_name)}
                      disabled={approving === r.bid_id}
                      data-testid={`final-approve-btn-${r.bid_id}`}
                      className="flex items-center gap-1.5 px-4 py-2 font-head text-[9px] uppercase tracking-[0.15em] font-black transition-all disabled:opacity-40 hover:brightness-110 shrink-0"
                      style={{ background: "#22C55E15", color: "#22C55E", border: "1px solid #22C55E40" }}>
                      {approving === r.bid_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />}
                      Final Approve
                    </button>
                  </div>

                  {r.receipt_url && (
                    <div>
                      {r.receipt_url.startsWith("data:image") ? (
                        <div className="relative group">
                          <img src={r.receipt_url} alt="Payment receipt"
                            className="max-h-48 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                            style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                            onClick={() => setPreviewUrl(r.receipt_url)} />
                          <button onClick={() => setPreviewUrl(r.receipt_url)}
                            className="absolute top-2 right-2 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ background: "rgba(0,0,0,0.7)" }}>
                            <ExternalLink className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <a href={r.receipt_url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 text-xs transition-opacity hover:opacity-80"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)" }}>
                          <FileText className="w-4 h-4" style={{ color: ACCENT }} />
                          View PDF Receipt
                        </a>
                      )}
                    </div>
                  )}
                  {!r.receipt_url && (
                    <div className="flex items-center gap-2 px-3 py-2"
                      style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.08)" }}>
                      <AlertCircle className="w-3.5 h-3.5" style={{ color: "rgba(255,255,255,0.3)" }} />
                      <p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>Receipt file is processing or unavailable</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Empty state ── */}
          {receipts.length === 0 && awaiting.length === 0 && (
            <div className="flex flex-col items-center py-10"
              style={{ border: "1px dashed rgba(255,255,255,0.08)" }}>
              <CheckCircle2 className="w-8 h-8 mb-2 opacity-10" />
              <p className="text-xs font-head uppercase tracking-[0.2em]"
                style={{ color: "rgba(255,255,255,0.2)" }}>No pending receipts</p>
            </div>
          )}
        </>
      )}

      {previewUrl && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.95)" }}
          onClick={() => setPreviewUrl(null)}>
          <img src={previewUrl} alt="Receipt preview" className="max-w-full max-h-full object-contain" />
          <button className="absolute top-4 right-4 p-2" style={{ color: "rgba(255,255,255,0.6)" }}
            onClick={() => setPreviewUrl(null)}><X className="w-5 h-5" /></button>
        </div>
      )}
    </div>
  );
}
